import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowUpRight,
  ArrowDownRight,
  Search,
  Filter,
  ExternalLink,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  Coins
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions`);
      setTransactions(response.data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "purchase":
        return <ArrowUpRight className="w-4 h-4" />;
      case "dividend":
        return <ArrowDownRight className="w-4 h-4" />;
      default:
        return <DollarSign className="w-4 h-4" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case "pending":
        return <Clock className="w-4 h-4 text-amber-600" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const filteredTransactions = transactions.filter((tx) => {
    const matchesSearch = tx.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          tx.transaction_hash.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || tx.type === filterType;
    const matchesStatus = filterStatus === "all" || tx.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const totalPurchases = transactions
    .filter(tx => tx.type === "purchase")
    .reduce((sum, tx) => sum + tx.amount, 0);

  const totalDividends = transactions
    .filter(tx => tx.type === "dividend")
    .reduce((sum, tx) => sum + tx.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Transaction History</h1>
          <p className="text-slate-500 mt-1">View all your blockchain transactions</p>
        </div>
        <Button variant="outline" data-testid="export-transactions-btn">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200" data-testid="stat-total-transactions">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Transactions</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{transactions.length}</p>
              </div>
              <div className="w-12 h-12 bg-[#E8F4FD] rounded-xl flex items-center justify-center">
                <Coins className="w-6 h-6 text-[#3B82F6]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-total-invested">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Invested</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{formatCurrency(totalPurchases)}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-total-received">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Dividends Received</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">{formatCurrency(totalDividends)}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <ArrowDownRight className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by project or transaction hash..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-transactions"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full md:w-40" data-testid="filter-type">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="purchase">Purchase</SelectItem>
            <SelectItem value="dividend">Dividend</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-40" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transactions List */}
      <Card className="border-slate-200" data-testid="transactions-list">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-4 px-6 font-medium text-slate-600">Type</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-600">Project</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-600">Amount</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-600">Tokens</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-600">Transaction Hash</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-600">Date</th>
                  <th className="text-left py-4 px-6 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tx-${tx.id}`}>
                    <td className="py-4 px-6">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                        tx.type === "purchase" 
                          ? "bg-blue-50 text-blue-700" 
                          : "bg-emerald-50 text-emerald-700"
                      }`}>
                        {getTypeIcon(tx.type)}
                        {tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <p className="font-medium text-slate-900">{tx.project_name}</p>
                    </td>
                    <td className="py-4 px-6">
                      <p className={`font-semibold ${
                        tx.type === "dividend" ? "text-emerald-600" : "text-slate-900"
                      }`}>
                        {tx.type === "dividend" ? "+" : ""}{formatCurrency(tx.amount)}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      {tx.tokens > 0 ? (
                        <p className="text-slate-900">{tx.tokens.toLocaleString()}</p>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <button
                        className="flex items-center gap-2 text-[#3B82F6] hover:underline"
                        onClick={() => window.open(`https://etherscan.io/tx/${tx.transaction_hash}`, "_blank")}
                        data-testid={`view-tx-${tx.id}`}
                      >
                        <code className="text-xs">
                          {tx.transaction_hash.slice(0, 10)}...{tx.transaction_hash.slice(-6)}
                        </code>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-sm text-slate-600">{formatDate(tx.timestamp)}</p>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(tx.status)}
                        <span className={`text-sm font-medium capitalize ${
                          tx.status === "completed" ? "text-emerald-600" :
                          tx.status === "pending" ? "text-amber-600" : "text-red-600"
                        }`}>
                          {tx.status}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTransactions.length === 0 && (
            <div className="text-center py-12">
              <Coins className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No transactions found.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blockchain Info */}
      <Card className="border-slate-200 bg-slate-50" data-testid="blockchain-info">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-sm text-slate-600">Connected to Ethereum Network</span>
            </div>
            <span className="text-xs text-slate-500">
              All transactions are recorded on the blockchain for transparency
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
